import {
  deleteAthread,
  handleFileDelete,
  LoadAllThreads,
  loadChatHistory,
  subscribeToChats,
  subscribeToFileUpload,
  subscribeToTitles,
  deleteFromCustomerDocuments,
} from "@/_services/customer-chat";
import { create } from "zustand";

// TODO : Move this to seperate file
interface ChatMessage {
  id: string;
  response?: string;
  request?: string;
  status?: "COMPLETED" | "INPROGRESS" | "FAILED";
}

interface Document {
  id: string;
  name: string;
  size: number;
  type: string;
  select: boolean;
  status: "COMPLETED" | "INPROGRESS" | "FAILED" | "CREATED";
}

interface Thread {
  id: string;
  created_by: string;
  created_at: string;
  title: string;
  customer_documents: Document[];
  select: boolean;
  thread_uuid: any;

  // Add other thread properties as needed
}

type ThreadsState = Record<string, Thread>;

// TODO : Move this to seperate file
interface HomepageState {
  message: string;
  chatHistory: Record<string, ChatMessage>;
  isMounted: boolean;
  threadId: string | null;
  documents: File[];

  prompts: Record<string, any>;
  threads: ThreadsState;
  deleteLoader: Record<string, boolean>;
  loadChatHistory: (threadId: string) => Promise<Record<string, ChatMessage>>;

  // Functions
  setMessage: (message: string) => void;
  loadAllThreads: (userId: string) => void;
  addChatMessage: (id: string, chatMessage: ChatMessage) => void;
  resetChatHistory: () => void;
  setThreadId: (threadId: string | null) => void;
  setPrompts: (prompts: Record<string, any>) => void;
  setDocuments: (documents: File[]) => void;
  subscribeToThread: (threadId: string) => Promise<void>;
  subscribeToThreadTitle: (threadId: string) => Promise<void>;
  resetPrompts: () => void;
  getDocuments: (threadId: string) => Document[];
  deleteThread: (threadId: string) => void;
  appendDocument: (threadId: string, document: Document[]) => void;
  updateDocuments: (threadId: string, documents: Document) => void;
  toggleFileStatus: (fileId: string, threadId: string) => void;
  deleteFile:(fileId: string, threadId: string,isFailedFile:boolean)=>void
}

export const useCustomerChatStore = create<HomepageState>((set, get) => ({
  message: "",
  chatHistory: {},
  isMounted: false,
  threadId: null,
  documents: [],
  prompts: {},
  threads: {},
  deleteLoader: {},

  // ------- ---- Chat Related function ---- ----
  setMessage: (message) => set({ message }),

  addChatMessage: (id, chatMessage) =>
    set((state) => ({
      chatHistory: { ...state.chatHistory, [id]: chatMessage },
    })),
  resetChatHistory: () => set({ chatHistory: {} }),
  // set current thread id
  setThreadId: (threadId) => set({ threadId }),

  setPrompts: (prompts) =>
    set(() => ({
      prompts: { ...prompts },
    })),

  resetPrompts: () => set({ prompts: {} }),

  // Set the documents
  setDocuments: (documents) => set({ documents }),

  // Subscribe to the thread get latest chat messages
  subscribeToThread: async (threadId) => {
    if (threadId) {
      await subscribeToChats(threadId, (payload) => {
        if (payload.status === "STREAMING") {
          set((state) => ({
            chatHistory: {
              ...state.chatHistory,
              [payload.id]: {
                ...state.chatHistory[payload.id],
                response: payload.response,
                request: payload.request,
                status: "STREAMING",
              },
            },
          }));
        }
        else if (payload.status === "COMPLETED") {
          set((state) => ({
            chatHistory: {
              ...state.chatHistory,
              [payload.id]: {
                ...state.chatHistory[payload.id],
                response: payload.response,
                request: payload.request,
                status: "COMPLETED",
              },
            },
          }));
        }
      });
    }
  },

  subscribeToThreadTitle: async (threadId) => {
    if (threadId) {
      await subscribeToTitles(threadId, (payload) => {
        if (payload.title) {
          // Check if the payload contains a title
          set((state) => ({
            threads: {
              ...state.threads,
              [payload.id]: {
                ...state.threads[payload.id],
                title: payload.title, // Update the title for the specific thread
              },
            },
          }));
        }
      });
    }
  },

  loadChatHistory: async (threadId: string) => {
    try {
      const response = await loadChatHistory(threadId);
      const chatHistory: Record<string, ChatMessage> = {};
      response.forEach((chat: ChatMessage) => {
        chatHistory[chat.id] = chat;
      });
      set({ chatHistory });
      return chatHistory;
    } catch (error) {
      throw error;
    }
  },

  /// ------- ---- THread related functions ---- ----

  getDocuments: (threadId) => {
    return get().threads[threadId]?.customer_documents || [];
  },

  loadAllThreads: async (userId) => {
    try {
      const response = await LoadAllThreads(userId);
      const currentThreads = get().threads; // Get the existing state from the store
      const threads: ThreadsState = {};
  
      response.forEach((thread: Thread) => {
        threads[thread.id] = {
          ...thread,
          customer_documents: thread.customer_documents.map((doc: any) => ({
            ...doc,
            select: currentThreads[thread.id]?.customer_documents.find(
              (currentDoc) => currentDoc.id === doc.id
            )?.select ?? true, // Initialize to true only if not already set
          })),
        };
      });
      set({ threads });
      return threads;
    } catch (error) {
      throw error;
    }
  },

  deleteThread: async (threadId) => {
    set((state) => ({
      deleteLoader: { ...state.deleteLoader, [threadId]: true },
    }));
    try {
      await deleteAthread(threadId);
      set((state) => {
        const newThreads = { ...state.threads };
        delete newThreads[threadId];
        const newDeleteLoader = { ...state.deleteLoader };
        delete newDeleteLoader[threadId];
        return { threads: newThreads, deleteLoader: newDeleteLoader };
      });
    } catch (error) {
      set((state) => ({
        deleteLoader: { ...state.deleteLoader, [threadId]: false },
      }));
      throw error;
    }
  },

  /// ------- ---- Document related functions ---- ----

  updateDocuments: (threadId, documents) => {
    set((state) => {
      const thread = state.threads[threadId];

        if (thread) {
          const updatedDocuments = thread.customer_documents.map((doc: any) => {
            if (doc.id === documents?.id) {
              if ((documents.status === 'FAILED')|| (documents.status === 'INPROGRESS')){
                return { ...documents};
              } else if (documents.status === 'COMPLETED') {
                return { ...documents, select: true };
              }
            }
            return doc;
          });

        return {
          threads: {
            ...state.threads,
            [threadId]: {
              ...thread,
              customer_documents: updatedDocuments as Document[],
            },
          },
        };
      }

      return state;
    });
  },

  toggleFileStatus: (fileId, threadId) => {
    // get().updatePromptsRank(fileId, false);
    set((state) => {
      const thread = state.threads[threadId];
      if (thread) {
        const updatedDocuments = thread.customer_documents.map((doc: any) =>
          doc.id === fileId ? { ...doc, select: !doc.select } : doc
        );

        // update the state to select :false
        return {
          threads: {
            ...state.threads,
            [threadId]: {
              ...thread,
              customer_documents: updatedDocuments as Document[],
            },
          },
        };
      }
      return state;
    });
  },

  appendDocument: (threadId, document) => {
    set((state) => {
      const thread = state.threads[threadId];

      const newDocuments = document.map((doc) => ({
        ...doc,
        select: true,
        status: "CREATED",
      }));
      if (thread) {
        return {
          threads: {
            ...state.threads,
            [threadId]: {
              ...thread,
              customer_documents: [
                ...thread.customer_documents,
                ...newDocuments,
              ] as any[],
            },
          },
        };
      }
      return state;
    });
  },

  deleteFile: async (threadId, fileId, isFailedFile = false) => {
    try {
      const documents = get().threads[threadId]?.customer_documents;
      if (!documents) {
        throw new Error("Thread documents not found");
      }
      if (!isFailedFile) {
        await handleFileDelete(fileId);
      } else {
        await deleteFromCustomerDocuments(fileId);
      }
      // Update the state after successful deletion
      set((state) => {
        const thread = state.threads[threadId];
        if (thread) {
          const updatedDocuments = thread.customer_documents.filter(
            (doc) => doc.id !== fileId
          );
          return {
            threads: {
              ...state.threads,
              [threadId]: {
                ...thread,
                customer_documents: updatedDocuments,
              },
            },
          };
        }
        return state;
      });
    } catch (error) {
      throw error;
    }
  },
}));
